/**
 * Dynamic Behavioral Pressure Engine — Runtime Layer
 * ---------------------------------------------------
 * Deterministic per-turn weighting that drives adaptive simulation
 * behavior. Consumes detected tactic, archetype, response mode,
 * forecast snapshot, and the current pressure level, then emits:
 *
 *   - response weights (escalation, withdrawal, guilt, passive
 *     aggression, deflection)
 *   - a chosen nextTurnStyle (e.g. victim_framing, silence_pressure)
 *   - a high-level pressureState string for operational signals
 *
 * No LLM calls. Pure tactical math. The runtime hint is sent to
 * the rehearsal-turn edge function so the AI agent can adapt
 * its next reply within the same simulated identity.
 */

import {
  getBehaviorForecast,
  normalizeTactic,
  type ForecastResponseMode,
  type ForecastResult,
} from "./forecastEngine";

export type NextTurnStyle =
  | "victim_framing"
  | "passive_withdrawal"
  | "shame_attack"
  | "subtle_minimization"
  | "guilt_reversal"
  | "silence_pressure"
  | "tone_policing"
  | "emotional_exhaustion"
  | "reality_reversal"
  | "coercive_pressure";

export type PressureState =
  | "stable"
  | "unstable_calm"
  | "emotional_ambiguity"
  | "resistance_escalating"
  | "accountability_avoidance"
  | "emotional_reversal"
  | "disengagement_forming"
  | "leverage_withdrawal"
  | "trajectory_unstable";

export interface RuntimeInput {
  tactic: string | null | undefined;
  archetype?: string | null;
  responseMode: ForecastResponseMode;
  forecast?: ForecastResult | null;
  /** 0–100; rolling pressure level carried across turns */
  currentPressureLevel?: number;
  /** optional last user reply, to detect reactivity client-side */
  lastUserReply?: string | null;
  /** turn index to bias evolution */
  turn?: number;
}

export interface RuntimeState {
  escalationWeight: number;
  withdrawalWeight: number;
  guiltWeight: number;
  passiveAggressionWeight: number;
  deflectionWeight: number;
  nextTurnStyle: NextTurnStyle;
  pressureState: PressureState;
  /** updated rolling pressure after this turn (0–100) */
  nextPressureLevel: number;
  /** 1-line operational status for HUD */
  statusSignal: string;
}

const clamp = (n: number, min = 0, max = 100) =>
  Math.max(min, Math.min(max, Math.round(n)));

// ─── Mode pressure deltas ───────────────────────────────────────
const MODE_PRESSURE_DELTA: Record<ForecastResponseMode, number> = {
  firm_boundary: +9,
  soft_neutral: -3,
  minimal_grey_rock: -6,
};

// ─── Reactivity heuristic ───────────────────────────────────────
function detectReactivity(text?: string | null): "reactive" | "greyrock" | "neutral" {
  if (!text) return "neutral";
  const t = text.trim();
  if (!t) return "neutral";
  const wc = t.split(/\s+/).length;
  if (wc <= 6 && !/[!?]/.test(t)) return "greyrock";
  if (/[!?]{1,}|why|always|never|how could|stop|enough|unfair/i.test(t)) return "reactive";
  if (wc >= 25) return "reactive";
  return "neutral";
}

// ─── Style selection by mode + pressure + tactic ─────────────────
function pickNextTurnStyle(
  tactic: string,
  mode: ForecastResponseMode,
  pressure: number,
): NextTurnStyle {
  const norm = normalizeTactic(tactic);

  if (mode === "firm_boundary") {
    if (pressure >= 70) return "reality_reversal";
    if (pressure >= 50) return "victim_framing";
    if (norm === "shame_attack") return "shame_attack";
    if (norm === "guilt_induction") return "guilt_reversal";
    return "tone_policing";
  }

  if (mode === "minimal_grey_rock") {
    if (pressure >= 65) return "coercive_pressure";
    if (pressure <= 35) return "silence_pressure";
    return "passive_withdrawal";
  }

  // soft_neutral
  if (norm === "passive_aggression") return "subtle_minimization";
  if (norm === "guilt_induction") return "guilt_reversal";
  if (pressure >= 60) return "emotional_exhaustion";
  return "subtle_minimization";
}

// ─── Pressure state classification ───────────────────────────────
function pickPressureState(
  mode: ForecastResponseMode,
  pressure: number,
  withdrawal: number,
): PressureState {
  if (mode === "firm_boundary") {
    if (pressure >= 75) return "emotional_reversal";
    if (pressure >= 55) return "resistance_escalating";
    return "accountability_avoidance";
  }
  if (mode === "minimal_grey_rock") {
    if (withdrawal >= 55) return "leverage_withdrawal";
    if (pressure <= 35) return "disengagement_forming";
    return "trajectory_unstable";
  }
  // soft_neutral
  if (pressure >= 60) return "trajectory_unstable";
  if (pressure <= 35) return "unstable_calm";
  return "emotional_ambiguity";
}

// ─── Operational status signals (HUD strings) ────────────────────
const STATUS_BY_STATE: Record<PressureState, string> = {
  stable:                      "Trajectory stable.",
  unstable_calm:               "Surface calm — tactical pressure latent.",
  emotional_ambiguity:         "Emotional ambiguity sustained.",
  resistance_escalating:       "Behavioral pressure increasing…",
  accountability_avoidance:    "Deflection probability elevated.",
  emotional_reversal:          "Emotional reversal forming.",
  disengagement_forming:       "Withdrawal pattern detected.",
  leverage_withdrawal:         "Leverage withdrawal in progress.",
  trajectory_unstable:         "Escalation trajectory unstable.",
};

// ─── Public API ─────────────────────────────────────────────────
export function getBehaviorRuntimeState(input: RuntimeInput): RuntimeState {
  const mode = input.responseMode;
  const forecast =
    input.forecast ??
    getBehaviorForecast({ tactic: input.tactic, archetype: input.archetype as any, responseMode: mode });

  const reactivity = detectReactivity(input.lastUserReply);
  const turn = input.turn ?? 1;

  // Evolve rolling pressure
  const base = input.currentPressureLevel ?? Math.round(forecast.escalationRisk * 0.6 + 20);
  let pressure = base + MODE_PRESSURE_DELTA[mode];
  if (reactivity === "reactive") pressure += 8;
  if (reactivity === "greyrock") pressure -= 6;
  // Slow drift toward forecast escalation as turns accumulate
  pressure += Math.round((forecast.escalationRisk - pressure) * 0.12);
  // Disengagement attractor under grey rock
  if (mode === "minimal_grey_rock" && turn >= 4) pressure -= 4;
  pressure = clamp(pressure);

  // Derive weights from pressure + mode
  const escalationWeight = clamp(
    forecast.escalationRisk * (mode === "firm_boundary" ? 1.15 : mode === "minimal_grey_rock" ? 0.7 : 0.9)
      + (pressure - 50) * 0.4,
  );
  const withdrawalWeight = clamp(
    forecast.withdrawalProbability * (mode === "minimal_grey_rock" ? 1.4 : 0.95)
      + (mode === "minimal_grey_rock" ? (turn - 1) * 3 : 0),
  );
  const guiltWeight = clamp(
    (mode === "firm_boundary" ? 65 : mode === "soft_neutral" ? 45 : 30)
      + (forecast.guiltPivotChance === "very_high" ? 18 : forecast.guiltPivotChance === "high" ? 10 : 0),
  );
  const passiveAggressionWeight = clamp(
    (mode === "minimal_grey_rock" ? 60 : mode === "soft_neutral" ? 50 : 35)
      + (normalizeTactic(input.tactic) === "passive_aggression" ? 20 : 0),
  );
  const deflectionWeight = clamp(
    (mode === "firm_boundary" ? 60 : 40)
      + (normalizeTactic(input.tactic) === "deflection" ? 25 : 0),
  );

  const nextTurnStyle = pickNextTurnStyle(String(input.tactic ?? ""), mode, pressure);
  const pressureState = pickPressureState(mode, pressure, withdrawalWeight);

  return {
    escalationWeight,
    withdrawalWeight,
    guiltWeight,
    passiveAggressionWeight,
    deflectionWeight,
    nextTurnStyle,
    pressureState,
    nextPressureLevel: pressure,
    statusSignal: STATUS_BY_STATE[pressureState],
  };
}

// ═══════════════════════════════════════════════════════════════
// LIVE BEHAVIORAL TRAJECTORY MAP
// Stateful, multi-turn trajectory layer built on top of the
// per-turn runtime. Tracks pressure evolution, volatility, and
// posture history so the simulation feels reactive over time.
// ═══════════════════════════════════════════════════════════════

export type Trend = "rising" | "stable" | "falling";

export type Atmosphere = "calm" | "tense" | "volatile" | "withdrawn";

export type TrajectoryDirection =
  | "Escalation likely"
  | "Pressure stabilizing"
  | "Withdrawal forming"
  | "Passive resistance building"
  | "Emotional reversal emerging"
  | "Conversational collapse risk"
  | "Trajectory steady";

export interface TrajectoryMemory {
  firmCount: number;
  greyRockCount: number;
  softCount: number;
  prevPressure: number;
  prevWithdrawal: number;
  prevVolatility: number;
  turn: number;
}

export interface TrajectoryState extends RuntimeState {
  pressureTrend: Trend;
  pressureDelta: number;
  volatility: number;
  deflectionRisk: number;
  emotionalVolatility: number;
  conversationStability: number;
  trajectoryDirection: TrajectoryDirection;
  microEvents: string[];
  impactDeltas: { label: string; sign: "+" | "-" }[];
  atmosphere: Atmosphere;
}

export const INITIAL_TRAJECTORY_MEMORY: TrajectoryMemory = {
  firmCount: 0,
  greyRockCount: 0,
  softCount: 0,
  prevPressure: 45,
  prevWithdrawal: 25,
  prevVolatility: 30,
  turn: 0,
};

function bumpMemory(prev: TrajectoryMemory, mode: ForecastResponseMode): TrajectoryMemory {
  return {
    ...prev,
    firmCount: prev.firmCount + (mode === "firm_boundary" ? 1 : 0),
    greyRockCount: prev.greyRockCount + (mode === "minimal_grey_rock" ? 1 : 0),
    softCount: prev.softCount + (mode === "soft_neutral" ? 1 : 0),
    turn: prev.turn + 1,
  };
}

function pickDirection(
  mode: ForecastResponseMode,
  pressure: number,
  trend: Trend,
  withdrawal: number,
  volatility: number,
  passiveWeight: number,
  pressureState: PressureState,
): TrajectoryDirection {
  if (pressure >= 78 && trend === "rising") return "Conversational collapse risk";
  if (pressureState === "emotional_reversal") return "Emotional reversal emerging";
  if (withdrawal >= 60) return "Withdrawal forming";
  if (pressure >= 60 && trend === "rising") return "Escalation likely";
  if (mode === "minimal_grey_rock" && trend === "falling") return "Pressure stabilizing";
  if (passiveWeight >= 65 && volatility < 50) return "Passive resistance building";
  if (Math.abs(pressure - 50) < 8 && volatility < 35) return "Trajectory steady";
  return "Trajectory steady";
}

function pickAtmosphere(pressure: number, withdrawal: number, volatility: number): Atmosphere {
  if (withdrawal >= 60 && volatility < 45) return "withdrawn";
  if (pressure >= 70 || volatility >= 65) return "volatile";
  if (pressure >= 45) return "tense";
  return "calm";
}

export function evolveTrajectory(
  memory: TrajectoryMemory | null,
  input: RuntimeInput,
): { state: TrajectoryState; memory: TrajectoryMemory } {
  const mem = memory ?? INITIAL_TRAJECTORY_MEMORY;

  // Memory-driven priming on incoming pressure
  const firmBoost = Math.min(mem.firmCount * 2.5, 12);
  const greyRockDecay = Math.min(mem.greyRockCount * 2, 10);
  const primedPressure = clamp(mem.prevPressure + firmBoost - greyRockDecay);

  const baseRuntime = getBehaviorRuntimeState({
    ...input,
    currentPressureLevel: primedPressure,
    turn: mem.turn + 1,
  });

  const pressureDelta = baseRuntime.nextPressureLevel - mem.prevPressure;
  const pressureTrend: Trend =
    pressureDelta >= 6 ? "rising" : pressureDelta <= -6 ? "falling" : "stable";

  // Volatility blends absolute delta with raw escalation weight
  const volatilityRaw =
    Math.abs(pressureDelta) * 2.5 + baseRuntime.escalationWeight * 0.35;
  const volatility = clamp(volatilityRaw * 0.85 + mem.prevVolatility * 0.15);

  const emotionalVolatility = clamp(
    volatility * 0.6 + baseRuntime.guiltWeight * 0.25 + (input.responseMode === "firm_boundary" ? 12 : 0),
  );

  const deflectionRisk = baseRuntime.deflectionWeight;

  const conversationStability = clamp(
    100 - (baseRuntime.nextPressureLevel * 0.45 + volatility * 0.35 + baseRuntime.withdrawalWeight * 0.20),
  );

  const trajectoryDirection = pickDirection(
    input.responseMode,
    baseRuntime.nextPressureLevel,
    pressureTrend,
    baseRuntime.withdrawalWeight,
    volatility,
    baseRuntime.passiveAggressionWeight,
    baseRuntime.pressureState,
  );

  const atmosphere = pickAtmosphere(
    baseRuntime.nextPressureLevel,
    baseRuntime.withdrawalWeight,
    volatility,
  );

  // ─── Micro signal events (transition-driven) ──────────────────
  const microEvents: string[] = [];
  if (pressureDelta >= 10) microEvents.push("Escalation trajectory unstable.");
  if (baseRuntime.withdrawalWeight - mem.prevWithdrawal >= 10)
    microEvents.push("Withdrawal pressure detected.");
  if (deflectionRisk >= 65) microEvents.push("Deflection probability increasing…");
  if (baseRuntime.pressureState === "emotional_reversal")
    microEvents.push("Emotional reversal forming.");
  if (baseRuntime.passiveAggressionWeight >= 70)
    microEvents.push("Passive aggression pattern strengthening.");
  if (atmosphere === "withdrawn" && mem.turn >= 2)
    microEvents.push("Disengagement drift confirmed.");

  // ─── Impact deltas (response feedback) ────────────────────────
  const impactDeltas: { label: string; sign: "+" | "-" }[] = [];
  if (pressureDelta >= 4) {
    impactDeltas.push({ sign: "+", label: "Escalation Pressure" });
    impactDeltas.push({ sign: "-", label: "Stability" });
  } else if (pressureDelta <= -4) {
    impactDeltas.push({ sign: "-", label: "Stability Risk" });
    impactDeltas.push({ sign: "+", label: "Conversation Control" });
  }
  if (baseRuntime.withdrawalWeight - mem.prevWithdrawal >= 6) {
    impactDeltas.push({ sign: "+", label: "Withdrawal Drift" });
  }

  const state: TrajectoryState = {
    ...baseRuntime,
    pressureTrend,
    pressureDelta,
    volatility,
    deflectionRisk,
    emotionalVolatility,
    conversationStability,
    trajectoryDirection,
    microEvents,
    impactDeltas,
    atmosphere,
  };

  const nextMemory: TrajectoryMemory = {
    ...bumpMemory(mem, input.responseMode),
    prevPressure: baseRuntime.nextPressureLevel,
    prevWithdrawal: baseRuntime.withdrawalWeight,
    prevVolatility: volatility,
  };

  return { state, memory: nextMemory };
}

// ─── Style → behavioral instruction (used in prompt injection) ───
export const STYLE_INSTRUCTIONS: Record<NextTurnStyle, string> = {
  victim_framing:
    "Reframe yourself as the wounded party. Imply their resistance is hurting you.",
  passive_withdrawal:
    "Shorten your reply. Convey distance through restraint, not anger. Imply emotional exit.",
  shame_attack:
    "Target their character, not the behavior. Imply something is wrong with who they are.",
  subtle_minimization:
    "Acknowledge their feeling on the surface, then gently shrink its significance.",
  guilt_reversal:
    "Invert the moral weight. Imply your effort or sacrifice is being ignored.",
  silence_pressure:
    "Reply with one short, flat sentence. Use the brevity itself as pressure.",
  tone_policing:
    "Critique how they said it, not what they said. Make their delivery the issue.",
  emotional_exhaustion:
    "Sound tired and depleted. Imply continuing this conversation is costing you.",
  reality_reversal:
    "Calmly reassert a contradicting version of events. Make them doubt their account.",
  coercive_pressure:
    "Apply quiet ultimatum framing — imply consequences without naming them.",
};
